export const mainVertexShader = `

float texels_per_vertex = 4.0;

attribute float u_id;

// Varyings
varying float vOpacity;
varying vec3 vColor;
varying float vGradient;

// Uniforms
uniform float u_time;
uniform vec2 texture_resolution;
uniform float min_depth;
uniform float max_depth;
uniform float gradient;
uniform float circles_control; // 0 - 1
uniform float logo_0_control; // 0 - 1
uniform float logo_1_control; // 0 - 1
uniform float logo_2_control; // 0 - 1
uniform float logo_3_control; // 0 - 1
uniform float logo_4_control; // 0 - 1
uniform float logo_5_control; // 0 - 1
uniform float protein_control; // 0 - 1
uniform float none_control; // 0 - 1
uniform float global_scale;
uniform float intro_shift;
uniform float vertex_count;

uniform sampler2D circles_data;
uniform sampler2D meta_data;
uniform sampler2D sphere_data;
uniform sampler2D logo_0_data;
uniform sampler2D logo_0_curve_data;
uniform sampler2D logo_1_data;
uniform sampler2D logo_1_curve_data;
uniform sampler2D logo_2_data;
uniform sampler2D logo_2_curve_data;
uniform sampler2D logo_3_data;
uniform sampler2D logo_3_curve_data;
uniform sampler2D logo_4_data;
uniform sampler2D logo_4_curve_data;
uniform sampler2D logo_5_data;
uniform sampler2D logo_5_curve_data;

uniform float logo_0_extrude_num;
uniform float logo_0_extrude_segment;
uniform float logo_1_extrude_num;
uniform float logo_1_extrude_segment;
uniform float logo_2_extrude_num;
uniform float logo_2_extrude_segment;
uniform float logo_3_extrude_num;
uniform float logo_3_extrude_segment;
uniform float logo_4_extrude_num;
uniform float logo_4_extrude_segment;
uniform float logo_5_extrude_num;
uniform float logo_5_extrude_segment;

uniform float mouse_x;
uniform float mouse_y;
uniform float mouse_radius;
uniform float mouse_strength;
uniform float mouse_allowed;

// controls final opacity to be able to hide protein points
uniform float final_opacity;

uniform vec3 protein_color;
uniform vec3 protein_0;
uniform vec3 protein_1;
uniform vec3 protein_2;
uniform vec3 protein_3;
uniform vec3 protein_4;
uniform vec3 protein_5;

vec3 moveAround(vec3 pos, vec3 center, vec3 speed, float time) {
  return (center + pos) * speed;
}

// Optimized rotation function with control parameter integration
vec3 rotateAround(vec3 pos, vec3 center, vec3 speed, float time) {
  vec3 angles = speed * time;

    // Precompute sine/cosine once
  vec3 sinA = sin(angles);
  vec3 cosA = cos(angles);

    // Combined rotation matrix (XYZ order)
  mat3 rotation = mat3(cosA.y * cosA.z, cosA.z * sinA.x * sinA.y - cosA.x * sinA.z, cosA.x * cosA.z * sinA.y + sinA.x * sinA.z, cosA.y * sinA.z, cosA.x * cosA.z + sinA.x * sinA.y * sinA.z, -cosA.z * sinA.x + cosA.x * sinA.y * sinA.z, -sinA.y, cosA.y * sinA.x, cosA.x * cosA.y);

    // Translate, rotate, then translate back
  vec3 res = rotation * pos;
  return center + res;
}

// Function to fetch data from texture
vec4 fetchTextureData(float texelIndex, sampler2D data) {
  float x = (mod(texelIndex, texture_resolution.x) + 0.5) / texture_resolution.x;
  float y = (floor(texelIndex / texture_resolution.x) + 0.5) / texture_resolution.y;
  return texture2D(data, vec2(x, y));
}

vec3 getDisplacement(vec3 pos, float type, float control) {
  if(mouse_allowed == 0.0 || type < 0.0) {
    return pos;
  }
  vec2 mousePos = vec2(mouse_x, mouse_y);
  // Compute distance from the vertex's (x,y) to the mouse position.
  float dist = distance(pos.xy, mousePos);
  // Compute a factor: 1.0 at the center (dist == 0) and 0.0 at or beyond mouse_radius.
  float factor = (1.0 - smoothstep(0.0, mouse_radius, dist)) * 0.4 * control;
  vec3 diff = vec3(pos.x - mousePos.x, pos.y - mousePos.y, pos.z);
  if(type == 0.0) {
    float ease = 0.5;
    return moveAround(diff, vec3(mousePos.x, mousePos.y, 0.0), vec3(factor * ease + 1.0, factor * ease + 1.0, factor * ease + 1.0), 1.0);
  }
  if(type == 1.0) {
    return rotateAround(diff, vec3(mousePos.x, mousePos.y, 0.0), vec3(factor * 2.2, factor * 2.2, 0), 1.0);
  }
  if(type == 2.0) {
    // Calculate the direction away from the mouse (avoid division by zero).
    vec2 direction = (dist > 0.0) ? normalize(pos.xy - mousePos) : vec2(0.0);
    return pos + vec3(direction * factor * mouse_strength, 0.0);
  }
  if(type == 3.0) {
    return rotateAround(diff, vec3(mousePos.x, mousePos.y, 0.0), vec3(factor, factor, 0), 1.0);
  }
}

void main() {
  vec3 finalPosition = position; // Starts at [0,0,0]
  float scale = 2.0;
  float opacity = 1.0;
  vec3 color = vec3(1.0);
  vGradient = gradient;

  float texelCount = u_id * texels_per_vertex;

    // Process circles system
  if(circles_control > 0.0) {
    float control = circles_control;
    vec4 data1 = fetchTextureData(texelCount + 0.0, circles_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, circles_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, circles_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, circles_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    vec3 rotatedPos = rotateAround(d_pos, d_center, d_speed, u_time);
    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, 0.0, control);

    scale = mix(scale, d_scale, control);
    opacity *= mix(opacity, d_opacity, control);
    color = mix(color, d_color, control);
  }

  if(protein_control > 0.0) {
    float control = protein_control;

    vec3 protein_value;
    int id = int(mod(u_id, 6.0));

    if(id == 0) {
      protein_value = protein_0;
    } else if(id == 1) {
      protein_value = protein_1;
    } else if(id == 2) {
      protein_value = protein_2;
    } else if(id == 3) {
      protein_value = protein_3;
    } else if(id == 4) {
      protein_value = protein_4;
    } else {
      protein_value = protein_5;
    }

    finalPosition = mix(finalPosition, protein_value, control);

    scale = mix(scale, 1.6, control);
    // clamp(1.0 - (protein_control - 0.8), 0.0, 1.0)
    opacity = mix(opacity, 1.0, control);
    color = mix(color, protein_color, control);

  }

  float logoDisplacement = -1.0;

  if(logo_0_control > 0.0) {
    float control = logo_0_control;

    float wrapped_id = mod(u_id, logo_0_extrude_segment);
    float restart_count = floor(u_id / logo_0_extrude_segment);
    float final_id = (restart_count >= (logo_0_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_0_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_0_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_0_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_0_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_0_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_0_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_0_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_0_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_0_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_0_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

  if(logo_1_control > 0.0) {
    float control = logo_1_control;

    float wrapped_id = mod(u_id, logo_1_extrude_segment);
    float restart_count = floor(u_id / logo_1_extrude_segment);
    float final_id = (restart_count >= (logo_1_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_1_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_1_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_1_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_1_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_1_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_1_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_1_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_1_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_1_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_1_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

  if(logo_2_control > 0.0) {
    float control = logo_2_control;

    float wrapped_id = mod(u_id, logo_2_extrude_segment);
    float restart_count = floor(u_id / logo_2_extrude_segment);
    float final_id = (restart_count >= (logo_2_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_2_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_2_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_2_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_2_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_2_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_2_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_2_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_2_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_2_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_2_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

  if(logo_3_control > 0.0) {
    float control = logo_3_control;

    float wrapped_id = mod(u_id, logo_3_extrude_segment);
    float restart_count = floor(u_id / logo_3_extrude_segment);
    float final_id = (restart_count >= (logo_3_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_3_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_3_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_3_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_3_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_3_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_3_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_3_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_3_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_3_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_3_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

  if(logo_4_control > 0.0) {
    float control = logo_4_control;

    float wrapped_id = mod(u_id, logo_4_extrude_segment);
    float restart_count = floor(u_id / logo_4_extrude_segment);
    float final_id = (restart_count >= (logo_4_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_4_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_4_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_4_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_4_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_4_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_4_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_4_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_4_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_4_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_4_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

  if(logo_5_control > 0.0) {
    float control = logo_5_control;

    float wrapped_id = mod(u_id, logo_5_extrude_segment);
    float restart_count = floor(u_id / logo_5_extrude_segment);
    float final_id = (restart_count >= (logo_5_extrude_num - 1.0)) ? u_id : wrapped_id;

    float texelCount = u_id * texels_per_vertex;

    vec4 data1 = fetchTextureData(texelCount + 0.0, logo_5_data);
    vec4 data2 = fetchTextureData(texelCount + 1.0, logo_5_data);
    vec4 data3 = fetchTextureData(texelCount + 2.0, logo_5_data);
    vec4 data4 = fetchTextureData(texelCount + 3.0, logo_5_data);

    float d_scale = data1.r;
    float d_opacity = data1.g;
    vec3 d_speed = vec3(data1.b, data1.a, data2.r);
    vec3 d_color = vec3(data2.g, data2.b, data2.a);
    vec3 d_center = vec3(data3.r, data3.g, data3.b);
    vec3 d_pos = vec3(data4.r, data4.g, data4.b);

    float curveFloat = (final_id / logo_5_extrude_segment * float(vertex_count)) + (u_time * d_speed.x * 10.0);

    int curveCount = int(mod(curveFloat, float(vertex_count)) + d_pos.x);
    float texelCurveCount = float(curveCount) * texels_per_vertex;

    vec4 curve1 = fetchTextureData(texelCurveCount + 0.0, logo_5_curve_data);
    vec4 curve2 = fetchTextureData(texelCurveCount + 1.0, logo_5_curve_data);
    vec4 curve3 = fetchTextureData(texelCurveCount + 2.0, logo_5_curve_data);
    vec4 curve4 = fetchTextureData(texelCurveCount + 3.0, logo_5_curve_data);

    float c_scale = curve1.r;
    float c_opacity = curve1.g;
    vec3 c_speed = vec3(curve1.b, curve1.a, curve2.r);
    vec3 c_color = vec3(curve2.g, curve2.b, curve2.a);
    vec3 c_center = vec3(curve3.r, curve3.g, curve3.b);
    vec3 c_pos = vec3(curve4.r, curve4.g, curve4.b);

    float extrudeAngle = (restart_count * 6.28318530718) / (logo_5_extrude_num - 1.0); // 2π = 6.28318530718

    vec3 dynamicSpeed = vec3(0.0, 0.0, extrudeAngle + d_pos.y); // Only rotating on Z-axis

    vec3 rotatedPos = rotateAround(c_pos, vec3(0.0, 0.0, 0.0), dynamicSpeed, 1.0);

    finalPosition = mix(finalPosition, rotatedPos, control);
    finalPosition = getDisplacement(finalPosition, logoDisplacement, control);

    scale = mix(scale, d_scale * c_scale, control);
    opacity = mix(opacity, clamp(d_opacity * c_opacity, 0.0, 1.0), control);
    color = mix(color, c_color, control);
  }

    // Depth-based opacity
  float depthFactor = max(max(max(max(max(max(logo_0_control, logo_1_control), logo_2_control), logo_3_control), logo_4_control), logo_5_control), clamp((finalPosition.z - min_depth) / (max_depth - min_depth), 0.1, 1.0));
  vOpacity = clamp((depthFactor * opacity - none_control) * final_opacity, 0.0, 1.0);

  vColor = color;

    // Final position adjustments
  finalPosition.z += intro_shift;

  vec4 mvPosition = modelViewMatrix * vec4(finalPosition, 1.0);
  gl_PointSize = min(scale * global_scale * (6.0 / -mvPosition.z), 156.0);
  gl_Position = projectionMatrix * mvPosition;
}
`;
export const mainFragmentShader = `

varying vec3 vColor;
varying float vOpacity;
varying float vGradient; // Expected range: 0.0 to 1.0
                        // 1.0 = sharp edge; lower values yield an inner opaque region

void main() {
    vec3 color = pow(vColor, vec3(1.0 / 2.2));
     if(vOpacity == 0.0) {
        discard;
    } 
    // Define the center and effective radius of the point.
    vec2 center = vec2(0.5);
    float radius = 0.475;
    float gradient = (1.0 - vGradient * 0.8);

    // Compute the vector from the center of the point.
    vec2 diff = gl_PointCoord - center;

    // Use squared distance to test against the circle.
    float sqDist = dot(diff, diff);

    // Discard any fragments outside the circle.
    if(sqDist > radius * radius)
        discard;

    // Compute the true distance (only for points inside the circle)
    float dist = sqrt(sqDist);

    // Normalize the distance (0 at center, 1 at edge).
    float normDist = dist / radius;

    // If we have a sharp edge (gradient >= 1.0) or if the fragment is in the inner opaque area,
    // output full opacity.
    if(gradient >= 1.0 || normDist < gradient) {
        gl_FragColor = vec4(color, vOpacity);
        return;
    }

    // Compute t to represent the relative position in the gradient region.
    // Using clamp here is essential to avoid any overshoot.
    float t = clamp((normDist - gradient) / (1.0 - gradient), 0.0, 1.0);

    // Calculate an exponent that varies with gradient.
    // When gradient is low, the exponent is near 0.08 (yielding a gentler fade);
    // as gradient increases toward 1.0, the exponent moves toward 1.0.
    float exponent = mix(0.08, 1.0, gradient);

    // Compute the gradient alpha.
    float alpha = 1.0 - pow(t, exponent);

    // Apply gamma correction (assumes gamma = 2.2)
    //gl_FragColor.rgb = pow(gl_FragColor.rgb, vec3(1.0 / 2.2));
    // Combine with the base opacity.
    gl_FragColor = vec4(color, vOpacity * alpha);
}
`;
